import * as THREE from 'three';

export const VERTICAL_EDGE_EPS = 1e-4;
export const DEFAULT_WALL_EDGE_COLOR = '#1a1a1a';
export const DEFAULT_WALL_EDGE_OPACITY = 0.45;

/**
 * @typedef {{
 *   showVerticalWallEdges?: boolean;
 *   showHorizontalWallEdges?: boolean;
 *   color?: string;
 *   opacity?: number;
 *   thresholdAngle?: number;
 *   depthTest?: boolean;
 * }} WallEdgeOptions
 */

/**
 * @param {WallEdgeOptions} [options]
 * @returns {Required<WallEdgeOptions>}
 */
export function resolveWallEdgeOptions(options = {}) {
  return {
    showVerticalWallEdges: options.showVerticalWallEdges === true,
    showHorizontalWallEdges: options.showHorizontalWallEdges === true,
    color: typeof options.color === 'string' ? options.color : DEFAULT_WALL_EDGE_COLOR,
    opacity: Number.isFinite(options.opacity) ? options.opacity : DEFAULT_WALL_EDGE_OPACITY,
    thresholdAngle: Number.isFinite(options.thresholdAngle) ? options.thresholdAngle : 1,
    depthTest: options.depthTest !== false,
  };
}

/**
 * @param {THREE.Vector3} a
 * @param {THREE.Vector3} b
 * @returns {boolean}
 */
export function isVerticalEdgeSegment(a, b) {
  const sameX = Math.abs(a.x - b.x) < VERTICAL_EDGE_EPS;
  const sameZ = Math.abs(a.z - b.z) < VERTICAL_EDGE_EPS;
  const verticalY = Math.abs(a.y - b.y) > VERTICAL_EDGE_EPS;
  return sameX && sameZ && verticalY;
}

/**
 * @param {THREE.Vector3} a
 * @param {THREE.Vector3} b
 * @returns {boolean}
 */
export function isHorizontalEdgeSegment(a, b) {
  const sameY = Math.abs(a.y - b.y) < VERTICAL_EDGE_EPS;
  const horizontalXZ =
    Math.abs(a.x - b.x) > VERTICAL_EDGE_EPS || Math.abs(a.z - b.z) > VERTICAL_EDGE_EPS;
  return sameY && horizontalXZ;
}

/**
 * @param {THREE.BufferGeometry} edgesGeometry
 * @param {(a: THREE.Vector3, b: THREE.Vector3) => boolean} predicate
 * @returns {THREE.BufferGeometry | null}
 */
function filterEdgeSegmentGeometry(edgesGeometry, predicate) {
  const position = edgesGeometry.attributes.position;
  const points = [];
  const tempA = new THREE.Vector3();
  const tempB = new THREE.Vector3();

  for (let i = 0; i < position.count; i += 2) {
    tempA.fromBufferAttribute(position, i);
    tempB.fromBufferAttribute(position, i + 1);
    if (predicate(tempA, tempB)) {
      points.push(tempA.x, tempA.y, tempA.z, tempB.x, tempB.y, tempB.z);
    }
  }

  edgesGeometry.dispose();

  if (points.length === 0) return null;

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
  return geometry;
}

/**
 * @param {THREE.BufferGeometry} sourceGeometry
 * @param {WallEdgeOptions} [options]
 * @param {boolean} includeVertical
 * @param {boolean} includeHorizontal
 * @param {'wall' | 'floor'} [edgeSurface]
 * @returns {THREE.LineSegments | null}
 */
export function createFilteredEdgesFromGeometry(
  sourceGeometry,
  options = {},
  includeVertical = false,
  includeHorizontal = false,
  edgeSurface = 'wall',
) {
  if (!sourceGeometry || (!includeVertical && !includeHorizontal)) return null;

  const resolved = resolveWallEdgeOptions(options);
  const edges = new THREE.EdgesGeometry(sourceGeometry, resolved.thresholdAngle);
  const geometry = filterEdgeSegmentGeometry(edges, (a, b) => {
    if (includeVertical && isVerticalEdgeSegment(a, b)) return true;
    if (includeHorizontal && isHorizontalEdgeSegment(a, b)) return true;
    return false;
  });

  if (!geometry) return null;

  const material = new THREE.LineBasicMaterial({
    color: resolved.color,
    transparent: resolved.opacity < 1,
    opacity: resolved.opacity,
    depthTest: resolved.depthTest,
  });

  const lines = new THREE.LineSegments(geometry, material);
  lines.renderOrder = 1;
  lines.userData.isWallEdgeOverlay = true;
  lines.userData.edgeSurface = edgeSurface;
  return lines;
}

/**
 * @param {THREE.BufferGeometry} sourceGeometry
 * @param {WallEdgeOptions} [options]
 * @returns {THREE.LineSegments | null}
 */
export function createVerticalEdgesFromGeometry(sourceGeometry, options = {}) {
  const resolved = resolveWallEdgeOptions(options);
  if (!resolved.showVerticalWallEdges) return null;
  return createFilteredEdgesFromGeometry(sourceGeometry, resolved, true, false, 'wall');
}

/**
 * @param {THREE.Object3D} mesh
 * @param {WallEdgeOptions} [options]
 * @param {{ surface?: 'wall' | 'floor' }} [context]
 * @returns {THREE.LineSegments | null}
 */
export function attachMeshEdges(mesh, options = {}, context = {}) {
  if (!(mesh instanceof THREE.Mesh) || !mesh.geometry) return null;

  const resolved = resolveWallEdgeOptions(options);
  const surface = context.surface === 'floor' ? 'floor' : 'wall';

  /** @type {THREE.LineSegments | null} */
  let lines = null;

  if (surface === 'wall') {
    lines = createFilteredEdgesFromGeometry(
      mesh.geometry,
      resolved,
      resolved.showVerticalWallEdges,
      resolved.showHorizontalWallEdges,
      'wall',
    );
  } else if (surface === 'floor' && resolved.showHorizontalWallEdges) {
    lines = createFilteredEdgesFromGeometry(mesh.geometry, resolved, false, true, 'floor');
  }

  if (lines) {
    mesh.add(lines);
  }

  return lines;
}
